import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel"

import { CardImage } from "@/components/brand-kit/card-image"
import { alternateCardImage } from "@/lib/card-image"
import Link from "next/link"
import { RecommendedProduct, compareByIllustratedThenPriced } from "@/lib/recommendations"
import { formatProductTitle } from "@/lib/breadcrumb-utils"
import { BadgeList } from "@/components/badges/badge-list"
import { badgesFromRecommended } from "@/components/badges/from-product"
import { productCardCtaName } from "@/lib/card-link-names"
import { dealCtaLabel, hasRenderedPrice } from "@/lib/deal-cta"

// Extracted to avoid re-creating objects on every render

interface RecentViewsBlockProps {
  products: RecommendedProduct[]
}

export function RecentViewsBlock({ products }: RecentViewsBlockProps) {
  if (!products || products.length === 0) {
    return null
  }

  // Illustrated first, then priced — the one rail rule, from
  // lib/recommendations.ts. This block used to carry its own copy of the
  // price-only comparator and re-sorted what the selector had already
  // ordered, which put un-illustrated cards back at the front (FRIDGE-398).
  const sortedProducts = [...products].sort(compareByIllustratedThenPriced)

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="pt-4 pb-1 px-4 sm:px-5">
        <h3 className="text-base sm:text-lg font-bold text-gray-900 line-clamp-2">
          👀 Your Recent Views on Fridge.com
          {products.length > 1 && (
            <span className="text-sm text-gray-500 ml-2">({products.length} products)</span>
          )}
        </h3>
        <p className="text-xs text-gray-600 mt-1">
          {products.length === 1 
            ? 'Top viewed product in this collection' 
            : 'Products you viewed on your visits'}
        </p>
      </div>
      <div className="px-2 sm:px-4 pb-4">
        {/* Scrollable carousel for unlimited products */}
        <Carousel
          opts={{
            align: "start",
            loop: false,
            slidesToScroll: 1,
            dragFree: true,
          }}
          className="w-full"
        >
          <CarouselContent className="-ml-2 sm:-ml-3">
            {sortedProducts.map((item) => {
              // FRIDGE-429 — ONE boolean gates the price row AND picks the CTA label.
              const showsPrice = hasRenderedPrice(item.price)
              return (
              <CarouselItem key={item.id} className="pl-2 sm:pl-3 basis-[280px] sm:basis-1/2 lg:basis-1/4">
                <div data-product-image-card className="border rounded-lg p-2.5 bg-white h-full flex flex-col hover:shadow-md transition-shadow">
                  <div className="relative mb-2">
                    {/* Canonical badge set with the client-side seen_before
                        overlay (spec §4.3). Same array + priority order as
                        everywhere else, so Price Drop etc. persist here too. */}
                    <BadgeList
                      badges={badgesFromRecommended(item, { seenBefore: true })}
                      variant="card"
                    />
                    <div className="aspect-square bg-white rounded-lg overflow-hidden">
                    <CardImage src={item.images?.[0] || '/placeholder.svg'} alternate={alternateCardImage(item.images?.[0] || '', item.images || [])} href={item.canonical_url || `/products/${item.id}`} alt={`${item.title} | Fridge.com`} width={200} height={200} />
                  </div>
                  </div>
                  <div className="text-xs text-[#0469f0] font-medium mb-1">Recently Viewed</div>
                  <Link href={item.canonical_url || `/products/${item.id}`} className="text-xs font-medium mb-2 line-clamp-2 min-h-[2rem] hover:underline">
                    {formatProductTitle(item.title)}
                  </Link>
                  <div className="mb-2">
                    {showsPrice && typeof item.price === 'number' ? (
                      <div className="flex items-baseline gap-1.5">
                        <span className={`font-bold text-base ${typeof item.originalPrice === 'number' && item.originalPrice > item.price ? 'text-green-600' : ''}`}>{`$${item.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</span>
                        {typeof item.originalPrice === 'number' && item.originalPrice > item.price && (
                          <span className="text-xs text-red-500 line-through">{`$${item.originalPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</span>
                        )}
                      </div>
                    ) : (
                      <div className="h-6" />
                    )}
                  </div>
                  <Link href={item.canonical_url || `/products/${item.id}`} aria-label={productCardCtaName(dealCtaLabel(showsPrice), item, item.canonical_url || `/products/${item.id}`)}>
                    <button className="w-full h-9 text-xs font-semibold whitespace-nowrap min-h-[36px] mt-auto bg-[#ff9951] hover:bg-[#ff8533] text-[var(--color-brand-action-text)] rounded-full transition-colors">
                      {dealCtaLabel(showsPrice)}
                    </button>
                  </Link>
                </div>
              </CarouselItem>
              )
            })}
          </CarouselContent>
        </Carousel>
      </div>
    </div>
  )
}

